import { SettingSectionHeader } from '@/sections/settings/components';
import { AccessLogsCards } from '@/sections/settings/components/cards/AccessLogsCards';
import { useAuth } from '@/state/auth';

/**
 * AccessLogsTab displays access logs for the account.
 *
 * @remarks
 * This component handles:
 * 1. **Access logs display** - Shows users who have accessed the account
 * 2. **Authentication method** - SSO vs local authentication display
 * 3. **Activity timestamps** - Last activity for each user
 *
 * Architecture follows ScanSettingsTab pattern:
 * - Simple tab wrapper (~40 lines)
 * - Complex logic in AccessLogsCards component
 * - Single unified section containing access logs table
 *
 * React 19 Features:
 * - Standard caching strategy with TanStack Query
 * - No unnecessary memoization (React Compiler handles optimization)
 *
 * @example
 * ```tsx
 * // Used in Settings page tabs
 * {selectedTab === 'accessLogs' && <AccessLogsTab />}
 * ```
 *
 * @see {@link AccessLogsCards} Core access logs component
 * @see {@link NotificationsTab} Reference pattern implementation
 */
export function AccessLogsTab() {
  const { isImpersonating, friend } = useAuth();

  return (
    <>
      {/* Access Logs Section */}
      <div className="flex flex-col gap-6">
        <SettingSectionHeader
          title="Access Logs"
          description="View the users who have accessed your account, including login times and authentication methods."
        />

        {/* Access Logs Cards - self-contained with data fetching */}
        <AccessLogsCards
          email={isImpersonating ? friend : undefined}
          editable={!isImpersonating}
        />
      </div>
    </>
  );
}
